import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import PriceListTable from "../VendorDetails/PriceList/PriceListTable";
import BackButton from "../../../../components/Button/BackButton";
import { AuthContext } from "../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../utils/constants/permissions";
import { vendorCode, vendorName } from "../../../../utils/vendorDisplay";

const MasterPriceListPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.VENDOR_PRICE_LIST);
  const [priceList, setPriceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetchPriceList = async () => {
      try {
        const res = await api.get("/vendor-price-list", { withCredentials: true });
        setPriceList(res.data.data || []);
        setError("");
      } catch (err) {
        console.error("Error fetching vendor master price list:", err);
        setError("Failed to load the price list");
      } finally {
        setLoading(false);
      }
    };
    fetchPriceList();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return priceList;

    return priceList.filter((item) => {
      const haystack = [
        item.vendor?.id ? vendorCode(item.vendor.id) : "",
        vendorName(item.vendor),
        item.service?.name,
        item.specialization?.name,
        item.currency?.currency?.code,
        item.unit,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [priceList, query]);

  const openInVendor = (item) => {
    if (!item.vendor?.id) return;
    navigate(`/vendors/${item.vendor.id}?tab=price-list&action=edit&id=${item.id}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 text-sm animate-pulse">Loading price list...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <BackButton />
          <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
            Vendor Price List
          </h1>
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search vendor, service, unit..."
          className="w-64 text-[13px] rounded-lg border border-gray-200 bg-white px-3 py-2 outline-none transition-colors placeholder:text-gray-400 hover:border-gray-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
      </div>

      {error && <p className="text-danger-600 text-sm mb-3">{error}</p>}

      <PriceListTable
        data={filtered}
        showVendor
        readOnly={readOnly}
        onEdit={openInVendor}
        emptyMessage={
          priceList.length === 0
            ? "No prices have been added for any vendor yet."
            : "No prices match your search."
        }
      />
    </>
  );
};

export default MasterPriceListPage;